/**
 * Post-processing for goggles firmware logs (logs/{uid}/{day}/goggles_{start}_{end}.txt).
 *
 * Shared by the frontend (src/utils/gogglesLog.js re-exports this file) and the backend agent
 * tools (dynamic import() from backend/agentTools). Pure: no imports, no I/O.
 *
 * The raw upload is not in time order. The firmware exports its ring buffer newest block
 * first, with records inside each block oldest first, and blocks are not delimited. A
 * "# session=<n> epoch=<seconds>" marker applies to every following record until the next
 * marker; `session` is a per-boot counter, `epoch` the boot's estimated Unix start (0 =
 * unknown), and the "(N)" on each record is milliseconds since boot. A drop in uptime
 * between adjacent lines is an older block of the same boot, not a reboot.
 *
 * So the chronological view groups records by session (oldest session first) and sorts each
 * session by uptime, with the original line number as the tie-breaker. Each session gets one
 * canonical anchor, the first nonzero epoch seen for it in the file, so the whole session is
 * on one clock; the other anchors are kept for diagnostics. A session with no nonzero anchor
 * keeps its uptimes and has no wall time. Nothing is dropped: `U (0)` records (timing
 * unknown), unparseable lines, and lines before the first marker are all kept, with source
 * line numbers, in their own sections.
 *
 * Times are rendered in Pacific time (ANALYTICS_TIMEZONE), the zone GA4 and the user
 * analytics pages use, so a log line and an analytics event at the same instant show the
 * same clock time. Full spec and fixtures: ble-logg-parsing/ble-log-postprocessing.md.
 */

// Self-contained on purpose: Node imports this file from CommonJS (backend/agentTools), where
// src/utils/timezone.js is not loadable, so the two helpers it needs are inlined here. They are
// copies of ANALYTICS_TIMEZONE and formatIsoInTimeZone in src/utils/timezone.js and must stay
// byte-for-byte equivalent in output.

/** IANA zone GA4 shards its `events_YYYYMMDD` tables on — see FIREBASE_ANALYTICS_TIMEZONE. */
export const ANALYTICS_TIMEZONE = 'America/Los_Angeles';

const offsetMsAt = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24, // Intl can emit "24" for midnight
    Number(parts.minute),
    Number(parts.second)
  );
  return asUTC - date.getTime();
};

/**
 * ISO 8601 string of an instant in a zone, with the zone's offset ("2026-09-22T20:00:30.687-07:00").
 * @param {Date|number} date
 * @param {string} timeZone
 * @returns {string}
 */
export const formatIsoInTimeZone = (date, timeZone) => {
  const ms = date instanceof Date ? date.getTime() : Number(date);
  if (!Number.isFinite(ms)) return '';

  // offsetMsAt reads the wall clock at whole-second precision, so measure it on a
  // whole-second instant and round to minutes; otherwise the ms fraction leaks in.
  const offsetMs = Math.round(offsetMsAt(new Date(Math.floor(ms / 1000) * 1000), timeZone) / 60000) * 60000;
  const wall = new Date(ms + offsetMs).toISOString().slice(0, -1); // drop the "Z"
  if (offsetMs === 0) return `${wall}Z`;

  const sign = offsetMs > 0 ? '+' : '-';
  const abs = Math.abs(offsetMs) / 60000;
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${wall}${sign}${hh}:${mm}`;
};

const SESSION_MARKER_RE = /^# session=(\d+) epoch=(\d+)\s*$/;
const LOG_LINE_RE = /^([A-Z]) \((\d+)\)(.*)$/;
const TAG_RE = /^\s*([^:]*):\s?(.*)$/;

/**
 * The two separate sources of error in a reconstructed goggles time (anchor + uptime).
 * Rounding is bounded; clock accuracy is not recorded anywhere. Shared by the chronological
 * download and the BLE Log Overlay so both say the same thing.
 */
export const TIME_ROUNDING_NOTE = 'Rounding: each boot anchor is stored in whole seconds, so a reconstructed '
  + 'time can be off by up to 1 s from rounding alone.';
export const CLOCK_ACCURACY_NOTE = 'Clock accuracy: anchors come from the goggles\u2019 clock. How far that clock '
  + 'was from real time, or from the phone clock GA4 uses, is not recorded, and that error adds to the rounding.';

const byUptimeThenLine = (a, b) => a.uptimeMs - b.uptimeMs || a.sourceLine - b.sourceLine;
const byLine = (a, b) => a.sourceLine - b.sourceLine;

/**
 * Parse a raw goggles log into sessions of chronologically ordered records.
 *
 * @param {string} content - raw goggles log text
 * @returns {{
 *   sessions: Array<{
 *     sessionId: number,
 *     canonicalEpoch: number|null,   // Unix seconds; null = no nonzero anchor in this session
 *     observedAnchors: number[],     // distinct nonzero anchors, in first-seen order
 *     anchorSpreadS: number,         // max - min of observedAnchors (0 when fewer than two)
 *     records: Array<GogglesLogRecord>,   // timed records, sorted by uptime then source line
 *     untimed: Array<GogglesLogRecord>,   // U (0) and unparseable lines, in source order
 *   }>,
 *   beforeFirstMarker: Array<GogglesLogRecord>,  // lines with no session to belong to
 *   hasMarker: boolean,
 * }}
 *
 * GogglesLogRecord: { sourceLine, raw, markerEpoch, sessionId, level, uptimeMs, tag, message,
 *   timestampMs, inferred }. `level`/`uptimeMs`/`tag`/`message` are null for unparseable
 *   lines; `uptimeMs` and `timestampMs` are null for U (0). `timestampMs` is null when the
 *   session has no anchor. `inferred` is true when the record's own marker had epoch 0 and
 *   its time comes from another marker in the same session.
 */
export const parseGogglesLog = (content) => {
  const lines = String(content ?? '').split(/\r?\n/);
  if (lines.length && lines[lines.length - 1] === '') lines.pop(); // trailing newline

  const sessionsById = new Map();
  const beforeFirstMarker = [];
  let current = null;
  let markerEpoch = null;

  lines.forEach((raw, index) => {
    const sourceLine = index + 1;

    const marker = SESSION_MARKER_RE.exec(raw);
    if (marker) {
      const sessionId = Number(marker[1]);
      markerEpoch = Number(marker[2]);
      if (!sessionsById.has(sessionId)) {
        sessionsById.set(sessionId, { sessionId, observedAnchors: [], records: [], untimed: [] });
      }
      current = sessionsById.get(sessionId);
      if (markerEpoch !== 0 && !current.observedAnchors.includes(markerEpoch)) {
        current.observedAnchors.push(markerEpoch);
      }
      return;
    }

    if (raw.trim() === '') return;

    const entry = LOG_LINE_RE.exec(raw);
    const record = {
      sourceLine,
      raw,
      markerEpoch: current ? markerEpoch : null,
      sessionId: current ? current.sessionId : null,
      level: null,
      uptimeMs: null,
      tag: null,
      message: null,
      timestampMs: null,
      inferred: false,
    };

    if (entry) {
      const [, level, uptime, rest] = entry;
      const tagged = TAG_RE.exec(rest);
      record.level = level;
      record.tag = tagged ? tagged[1] : null;
      record.message = tagged ? tagged[2] : rest.trim();
      // Firmware U (0) records carry no timing; 0 is not "at boot".
      if (!(level === 'U' && uptime === '0')) record.uptimeMs = Number(uptime);
    }

    if (!current) beforeFirstMarker.push(record);
    else if (record.uptimeMs === null) current.untimed.push(record);
    else current.records.push(record);
  });

  const sessions = [...sessionsById.values()]
    .sort((a, b) => a.sessionId - b.sessionId)
    .map((session) => {
      const canonicalEpoch = session.observedAnchors.length ? session.observedAnchors[0] : null;
      const anchorSpreadS = session.observedAnchors.length > 1
        ? Math.max(...session.observedAnchors) - Math.min(...session.observedAnchors)
        : 0;
      const records = session.records
        .map((r) => ({
          ...r,
          timestampMs: canonicalEpoch === null ? null : canonicalEpoch * 1000 + r.uptimeMs,
          inferred: canonicalEpoch !== null && r.markerEpoch === 0,
        }))
        .sort(byUptimeThenLine);
      return { ...session, canonicalEpoch, anchorSpreadS, records, untimed: session.untimed.sort(byLine) };
    });

  return { sessions, beforeFirstMarker, hasMarker: sessionsById.size > 0 };
};

const formatRecordLine = (record, timeZone) => {
  const time = record.timestampMs === null ? 'time unknown' : formatIsoInTimeZone(record.timestampMs, timeZone);
  const epoch = record.markerEpoch === null ? 'none' : record.markerEpoch;
  const parts = [time, `source_line=${record.sourceLine}`, `marker_epoch=${epoch}`];
  if (record.inferred) parts.push('inferred from another marker in this session');
  parts.push(record.raw);
  return parts.join(' | ');
};

const clockNote = (session) => {
  if (session.observedAnchors.length < 2) return null;
  if (session.anchorSpreadS <= 1) {
    return `Clock note: observed anchors differ by ${session.anchorSpreadS} s, within whole-second rounding. `
      + 'Canonical anchor is the first nonzero anchor in raw input.';
  }
  return `Clock warning: observed anchors differ by ${session.anchorSpreadS} s, more than whole-second rounding `
    + 'explains, so the goggles\u2019 clock changed during this boot. Canonical anchor is the first nonzero anchor '
    + 'in raw input; times in this session may be off by that much.';
};

/**
 * Render a raw goggles log as the chronological, Pacific-time view.
 *
 * @param {string} content - raw goggles log text
 * @param {string} [timeZone] - IANA zone for wall times; defaults to ANALYTICS_TIMEZONE
 * @returns {string|null} the processed text, or null if the file has no session marker
 */
export const formatChronologicalGogglesLog = (content, timeZone = ANALYTICS_TIMEZONE) => {
  const { sessions, beforeFirstMarker, hasMarker } = parseGogglesLog(content);
  if (!hasMarker) return null;

  const out = [
    '# Derived chronological view. Raw input is retained byte-for-byte.',
    `# Times are ${timeZone}, reconstructed as canonical anchor + uptime, one anchor per session.`,
    `# ${TIME_ROUNDING_NOTE}`,
    `# ${CLOCK_ACCURACY_NOTE}`,
    '# source_line refers to the original raw file, including marker lines.',
  ];

  sessions.forEach((session) => {
    const start = session.canonicalEpoch === null
      ? 'unknown'
      : formatIsoInTimeZone(session.canonicalEpoch * 1000, timeZone);
    out.push('');
    out.push(`Session ${session.sessionId} | canonical_epoch=${session.canonicalEpoch ?? 'unknown'} | start=${start}`);
    out.push(`Observed nonzero anchors: ${session.observedAnchors.length ? session.observedAnchors.join(', ') : 'none'}`);
    const note = clockNote(session);
    if (note) out.push(note);
    session.records.forEach((r) => out.push(formatRecordLine(r, timeZone)));
    if (session.untimed.length) {
      out.push('Untimed/unparseable records (chronological position unknown):');
      session.untimed.forEach((r) => out.push(formatRecordLine(r, timeZone)));
    }
  });

  if (beforeFirstMarker.length) {
    out.push('');
    out.push('Records before the first session marker (session and time unknown):');
    beforeFirstMarker.forEach((r) => out.push(formatRecordLine(r, timeZone)));
  }

  return `${out.join('\n')}\n`;
};
